import React, { useEffect, useRef } from "react";

import {
  useApiErrors,
  useAuth,
  useDisclose,
  useHookForm,
  useMerchantApiMutation,
} from "../../hooks";
import {
  getApiErrorsByFieldName,
  getUserDepartment,
  globalHandlerUtils,
} from "../../utils";
import {
  ICreateTransferPin,
  IViewAuthorization,
} from "../../interfaces-and-types";
import { appConstants } from "../../constants";
import { createTransferPinSchema } from "./validation-schema";
import { View } from "./view";
import { useNavigate, useNavigation } from "react-router-dom";

const PIN_FIELD_NAME = "pin";
const CONFIRM_PIN_FIELD_NAME = "confirm_pin";

export const CreateTransferPin = () => {
  const navigate = useNavigate();
  const { active_tenant_id, user, staff_departments, setStaffDepartments } =
    useAuth();

  const [isSuccessModalOpen, setIsSuccessModalOpen] = React.useState(false);

  let timeoutId = useRef<NodeJS.Timeout>();

  useEffect(() => {
    const activeStaffDept = getUserDepartment(
      active_tenant_id,
      staff_departments
    );

    if (activeStaffDept?.has_pin) {
      timeoutId.current = setTimeout(
        () =>
          window.confirm(
            // "PIN Setup",
            "You already have an active PIN created for this profile. Do you want to change your PIN?"
          )
            ? navigate("/settings/change-transfer-pin")
            : navigate("/settings"),
        1500
      );
    }

    return () => timeoutId && clearTimeout(timeoutId.current);
  }, [active_tenant_id]);

  const {
    isLoading: isLoadingCreateTransferPin,
    isSuccess: isSuccessCreateTransferPin,
    isError: isErrorCreateTransferPin,
    data: apiResponse,
    mutate: createTransferPin,
  } = useMerchantApiMutation<ICreateTransferPin, IViewAuthorization>(
    "createTransferPin"
  ).usePostMutation;

  useEffect(() => {
    if (isSuccessCreateTransferPin) {
      // update the dept profile
      const accountWithNewPin = getUserDepartment(
        active_tenant_id,
        staff_departments
      );

      setStaffDepartments([
        ...staff_departments!.filter((d) => d.merchant_id !== active_tenant_id),
        {
          ...accountWithNewPin!,
          has_pin: true,
        },
      ]);

      navigate("/settings");
    }
  }, [isSuccessCreateTransferPin]);

  const initialFormValues: ICreateTransferPin = {
    password: "",
    pin: "",
    confirm_pin: "",
  };

  const handleApiSubmit = (data: ICreateTransferPin) => {
    createTransferPin(data);
  };

  const handleError = (errors: any) => globalHandlerUtils.handleError(errors);

  const {
    onFormSubmit,
    errors,
    getValues,
    register,
    reset,
    setValue,
    setError,
    control,
    watch,
  } = useHookForm({
    initialFormValues,
    validationSchema: createTransferPinSchema,
    onValid: handleApiSubmit,
    onInvalid: handleError,
  });

  const formValues = watch();

  const { apiErrors } = useApiErrors();

  // enter new PIN
  const pinInputApiErrorMessage =
    getApiErrorsByFieldName(PIN_FIELD_NAME, apiErrors) || "";
  const hookFormErrorMessage =
    errors[PIN_FIELD_NAME] && errors[PIN_FIELD_NAME]?.["message"]?.toString();

  const combinedCreatePinInputErrors =
    hookFormErrorMessage || pinInputApiErrorMessage || null;

  const {
    isOpen: isOpenCreatePinInput,
    onClose: onCloseCreatePinInput,
    onOpen: onOpenCreatePinInput,
  } = useDisclose();

  const onCreatePinSubmit = (pin: string): boolean => {
    setValue(PIN_FIELD_NAME, pin);

    return true;
  };

  const onCreatePinCorrect = (): boolean => {
    onCloseCreatePinInput();
    if (!formValues.confirm_pin) setTimeout(onOpenConfirmPinInput, 500);

    return true;
  };

  // confirm PIN
  const combinedConfirmPinInputApiErrorMessage =
    getApiErrorsByFieldName(CONFIRM_PIN_FIELD_NAME, apiErrors) || "";
  const combinedConfirmPinHookFormErrorMessage =
    errors[CONFIRM_PIN_FIELD_NAME] &&
    errors[CONFIRM_PIN_FIELD_NAME]?.["message"]?.toString();

  const combinedConfirmPinInputErrors =
    combinedConfirmPinHookFormErrorMessage ||
    combinedConfirmPinInputApiErrorMessage ||
    null;

  const {
    isOpen: isOpenConfirmPinInput,
    onClose: onCloseConfirmPinInput,
    onOpen: onOpenConfirmPinInput,
  } = useDisclose();

  const onConfirmPinSubmit = (confirmPin: string): boolean => {
    setValue(CONFIRM_PIN_FIELD_NAME, confirmPin);

    const isPinConfirmMatch = formValues.pin === confirmPin;

    // if (!isPinConfirmMatch)
    //   setError(CONFIRM_PIN_FIELD_NAME, {
    //     message: "Pin Confirmation must be the same as PIN",
    //   });
    return isPinConfirmMatch;
  };

  const onConfirmPinCorrect = (): boolean => {
    onCloseConfirmPinInput();
    return true;
  };

  return (
    <View
      isModalOpen
      closeModal={() => navigate("/settings")}
      // create PIN
      isOpenCreatePinInput={isOpenCreatePinInput}
      onOpenCreatePinInput={onOpenCreatePinInput}
      onCloseCreatePinInput={onCloseCreatePinInput}
      combinedCreatePinInputErrors={combinedCreatePinInputErrors}
      onCreatePinSubmit={onCreatePinSubmit}
      onCreatePinCorrect={onCreatePinCorrect}
      // confirm PIN
      isOpenConfirmPinInput={isOpenConfirmPinInput}
      onOpenConfirmPinInput={onOpenConfirmPinInput}
      onCloseConfirmPinInput={onCloseConfirmPinInput}
      combinedConfirmPinInputErrors={combinedConfirmPinInputErrors}
      onConfirmPinSubmit={onConfirmPinSubmit}
      onConfirmPinCorrect={onConfirmPinCorrect}
      // hook form
      formValues={formValues}
      handleSubmit={onFormSubmit}
      register={register}
      errors={errors}
      control={control}
    />
  );
};
